import { createSlice } from "@reduxjs/toolkit";
import { addLeadSource, addLeadStatus, createLead, fetchLeadPurpose, fetchLeads, fetchLeadsPage, fetchLeadSource, fetchLeadStatus, updateLeadStatus } from "./leadThunk";

const getSavedLeadFilters = () => {
  try {
    const saved = sessionStorage.getItem("crmLeadFilters");
    return saved
      ? JSON.parse(saved)
      : {
          search: "",
          source: null,
          status: null,
          purpose: null,
          assigned_to: null,
          sort_by: "created_desc",
        };
  } catch {
    return {
      search: "",
      source: null,
      status: null,
      purpose: null,
      assigned_to: null,
      sort_by: "created_desc",
    };
  }
};

const DEFAULT_VISIBLE_COLUMNS = [
  "no",
  "name",
  "phone",
  "assigned_to",
  "status",
  "last_updated",
  "actions",
];

  const getSavedVisibleColumns = () => {
    try {
      const saved = sessionStorage.getItem("crmLeadVisibleColumns");
      return saved ? JSON.parse(saved) : DEFAULT_VISIBLE_COLUMNS;
    } catch {
      return DEFAULT_VISIBLE_COLUMNS;
    }
  };

const initialState = {
  list: [],
  loading: false,
  // The paged leads table (desktop list view) has its own page of leads, separate from `list`,
  // which stays the full list the pipeline view, mobile list and pickers use.
  pageList: [],
  pageTotal: 0,
  pageLoading: false,
  pageParams: { limit: 10, offset: 0 }, // the page requested last, reused by refreshLeads()
  pageRequestId: null, // only the newest page request may update pageList
  pagedActive: false, // true while the paged table is mounted
  createLoading: false,
  filters: {
    status: "ALL",
  },
  statusList: [],
  sourceList: [],
  purposeList: [],
  filterState: getSavedLeadFilters(),
  visibleColumns: getSavedVisibleColumns(),
};

const leadSlice = createSlice({
  name: "leads",
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };      
    },

    setLeadFilterState: (state, action) => {
      state.filterState = {
        ...state.filterState,
        ...action.payload,
      };
      sessionStorage.setItem(
        "crmLeadFilters",
        JSON.stringify(state.filterState)
      );
    },
    resetLeadFilterState: (state) => {
      state.filterState = {
        search: "",
        source: null,
        status: null,
        purpose: null,
        assigned_to: null,
        date_from: null,
        date_to: null,
        sort_by: "created_desc",
      };

      sessionStorage.removeItem("crmLeadFilters");
    },

    // Only for a genuinely fresh visit to the Leads page (PUSH navigation,
    // e.g. the sidebar or another module) -- NOT when the user is popping
    // back here from a lead's own detail page (POP navigation), where the
    // search is meant to still be showing. See the navigationType check in
    // index.js, the only caller.
    resetLeadSearchFilter: (state) => {
      if (!state.filterState.search) return; // no-op: keeps the same reference

      state.filterState = {
        ...state.filterState,
        search: "",
      };

      sessionStorage.setItem(
        "crmLeadFilters",
        JSON.stringify(state.filterState)
      );
    },

    setLeadsPaged: (state, action) => {
      state.pagedActive = action.payload;
    },

    setVisibleColumns: (state, action) => {
        state.visibleColumns = action.payload;
        sessionStorage.setItem(
            "crmLeadVisibleColumns",
            JSON.stringify(action.payload)
        );
    },

    resetVisibleColumns: (state) => {
        state.visibleColumns = DEFAULT_VISIBLE_COLUMNS;
        sessionStorage.removeItem("crmLeadVisibleColumns");
    },

  },
  
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeads.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchLeads.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(fetchLeads.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchLeadsPage.pending, (state, action) => {
        state.pageLoading = true;
        state.pageRequestId = action.meta.requestId;
        if (action.meta.arg) state.pageParams = action.meta.arg;
      })
      .addCase(fetchLeadsPage.fulfilled, (state, action) => {
        if (state.pageRequestId !== action.meta.requestId) return; // a newer request is in flight
        state.pageLoading = false;
        state.pageList = action.payload.rows;
        state.pageTotal = action.payload.total;
      })
      .addCase(fetchLeadsPage.rejected, (state, action) => {
        if (state.pageRequestId !== action.meta.requestId) return;
        state.pageLoading = false;
      })
      .addCase(updateLeadStatus.fulfilled, (state, action) => {
        const { id, action: act } = action.payload;

        [state.list, state.pageList].forEach((leads) => {
          const lead = leads.find((l) => l.id === id);
          if (lead) {
              if (act === "convert") {
                lead.status = {
                  label: "Converted",
                  color: "green",
                };
              }

              if (act === "reject") {
                lead.status = {
                  label: "Lost",
                  color: "red",
                };
              }
          }
        });
     })
     .addCase("leads/updateLocalStatus", (state, action) => {
      const { id, status_id } = action.payload;
      [state.list, state.pageList].forEach((leads) => {
        const lead = leads.find(l => l.id === id);
        if (lead) {
          lead.status_id = status_id;
        }
      });
    })
     .addCase(createLead.pending, (state) => {
        state.createLoading = true;
      })
      .addCase(createLead.fulfilled, (state) => {
        state.createLoading = false;
      })
      .addCase(createLead.rejected, (state) => {
        state.createLoading = false;
      })
      .addCase(fetchLeadStatus.fulfilled, (state, action) => {
        state.statusList = action.payload;
      })
      .addCase(fetchLeadSource.fulfilled, (state, action) => {
        state.sourceList = action.payload;
      })
      .addCase(addLeadStatus.pending, (state) => {
        state.createLoading = true;
      })
      .addCase(addLeadStatus.fulfilled, (state) => {
        state.createLoading = false;
      })
      .addCase(addLeadStatus.rejected, (state) => {
        state.createLoading = false;
      })
      .addCase(addLeadSource.pending, (state) => {
        state.createLoading = true;
      })
      .addCase(addLeadSource.fulfilled, (state) => {
        state.createLoading = false;
      })
      .addCase(addLeadSource.rejected, (state) => {
        state.createLoading = false;
      })
      .addCase(fetchLeadPurpose.fulfilled, (state, action) => {
        state.purposeList = action.payload;
      });
  },
});

export const { setFilters, setLeadFilterState, resetLeadFilterState, resetLeadSearchFilter, setLeadsPaged, setVisibleColumns, resetVisibleColumns } = leadSlice.actions;
export default leadSlice.reducer;